import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import type { components } from "../../lib/api-types.gen";

export type Category = components["schemas"]["Category"];
export type Area = components["schemas"]["Area"];
export type Severity = components["schemas"]["Severity"];
export type Tag = components["schemas"]["Tag"];

// Master data changes rarely; keep it fresh for a few minutes to avoid refetch
// churn while the incident screens mount and unmount.
const MASTER_DATA_STALE = 5 * 60_000;

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () => apiFetch<Category[]>("/categories"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useAreas() {
  return useQuery({
    queryKey: ["areas"],
    queryFn: () => apiFetch<Area[]>("/areas"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useSeverities() {
  return useQuery({
    queryKey: ["severities"],
    queryFn: () => apiFetch<Severity[]>("/severities"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: () => apiFetch<Tag[]>("/tags"),
    staleTime: MASTER_DATA_STALE,
  });
}

/* ---------------------------------------------------------------------------
 * Active-only picker hooks (F-009 / AC-010)
 *
 * The new-incident form must offer ONLY active master data, so a deactivated
 * entry can never land on a fresh incident. These hooks request the server's
 * ACTIVE-ONLY selection list (?includeInactive=false) under a distinct
 * ["<kind>", "active"] query key, kept separate from the full lists above that
 * ground name resolution on existing incidents (which must keep resolving the
 * names of since-deactivated entries). Admin master-data mutations invalidate
 * the ["<kind>"] prefix, which covers both variants.
 * ------------------------------------------------------------------------- */

export function useActiveCategories() {
  return useQuery({
    queryKey: ["categories", "active"],
    queryFn: () => apiFetch<Category[]>("/categories?includeInactive=false"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useActiveAreas() {
  return useQuery({
    queryKey: ["areas", "active"],
    queryFn: () => apiFetch<Area[]>("/areas?includeInactive=false"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useActiveSeverities() {
  return useQuery({
    queryKey: ["severities", "active"],
    queryFn: () => apiFetch<Severity[]>("/severities?includeInactive=false"),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useActiveTags() {
  return useQuery({
    queryKey: ["tags", "active"],
    queryFn: () => apiFetch<Tag[]>("/tags?includeInactive=false"),
    staleTime: MASTER_DATA_STALE,
  });
}

/**
 * The four ACTIVE-ONLY lists for the new-incident picker (F-009 / AC-010).
 * Distinct from {@link useMasterData}: that one loads the full lists (incl.
 * inactive) for name resolution; this one loads only what may be selected on a
 * new incident.
 */
export function useActiveMasterData() {
  const categories = useActiveCategories();
  const areas = useActiveAreas();
  const severities = useActiveSeverities();
  const tags = useActiveTags();

  return {
    categories,
    areas,
    severities,
    tags,
    isLoading:
      categories.isLoading ||
      areas.isLoading ||
      severities.isLoading ||
      tags.isLoading,
  };
}

export function toMap<T extends { id: string }>(
  list: readonly T[] | undefined,
): Map<string, T> {
  return new Map((list ?? []).map((item) => [item.id, item]));
}

/** Only the `active` entries filter-in for create-form pickers. */
export function activeOnly<T extends { active: boolean }>(
  list: readonly T[] | undefined,
): T[] {
  return (list ?? []).filter((item) => item.active);
}

/**
 * All four master-data lists plus id->entity maps for name resolution on the
 * list/detail screens. Loads every entry (including inactive) so historical
 * references still resolve to a name.
 */
export function useMasterData() {
  const categories = useCategories();
  const areas = useAreas();
  const severities = useSeverities();
  const tags = useTags();

  const categoryById = useMemo(() => toMap(categories.data), [categories.data]);
  const areaById = useMemo(() => toMap(areas.data), [areas.data]);
  const severityById = useMemo(() => toMap(severities.data), [severities.data]);
  const tagById = useMemo(() => toMap(tags.data), [tags.data]);

  return {
    categories,
    areas,
    severities,
    tags,
    categoryById,
    areaById,
    severityById,
    tagById,
    isLoading:
      categories.isLoading ||
      areas.isLoading ||
      severities.isLoading ||
      tags.isLoading,
  };
}
